import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * PlatformInActionSection — V22.9.10 slideshow rewrite
 *
 * Behavior:
 *   • Auto-rotating 6-slide carousel, 7s per slide, infinite loop.
 *   • Hover does NOT pause (was too easy to trigger by accident).
 *   • Pause is ONLY triggered by explicit user interaction:
 *       - clicking a progress dot
 *       - clicking prev / next chevron
 *       - swiping left/right on touch
 *       - ←/→ keyboard arrows when frame is focused
 *     → in all cases an 8s "polite pause" then auto-resume.
 *   • prefers-reduced-motion → autoplay still runs, just longer dwell + no fade.
 *   • document.hidden → pause (no wasted CPU when tab is backgrounded).
 *   • IntersectionObserver → pause when section is fully scrolled off-screen.
 *   • One useEffect, one setInterval driving both progress bar + slide flip
 *     (50ms tick — single source of truth, no clock drift).
 *
 * Accessibility / SEO:
 *   • role="region" + aria-roledescription="carousel"
 *   • Each slide is a role="group" with aria-label "N of 6: title"
 *   • Hidden <ul class="sr-only"> mirror keeps ALL 6 descriptions crawlable.
 *   • Active dot has aria-current="true"; aria-live="polite" announces flips.
 *
 * Performance:
 *   • Slide #1 rendered eagerly with fetchpriority="high".
 *   • Slide #2 preloaded via injected <link rel="preload">.
 *   • Slides #3–6 prefetched in idle callbacks (staggered, ~200ms apart).
 *   • Only two <img> in the DOM (current + next) — CSS cross-fade, no
 *     AnimatePresence remount cost.
 */

const SLIDE_MS = 4000;
const TICK_MS = 50;
const POLITE_PAUSE_MS = 8000;

const showcaseSlides = [
  {
    id: "mission-control",
    label: "COMMAND CENTER",
    title: "Mission Control Dashboard",
    description: "Programme Health Score, AI reminder pool, pending approvals, team activity & module shortcuts — your single pane of glass for the BCM lifecycle.",
    url: "/images/showcase/01_mission_control.png",
  },
  {
    id: "operational-bia",
    label: "BUSINESS IMPACT ASSESSMENT",
    title: "5×4 Impact Matrix",
    description: "Score No / Low / Medium / High impact across Financial, Legal, Regulatory, Reputational & Operational categories over 4 time-bands — the engine auto-derives RTO / RPO / MTPD.",
    url: "/images/showcase/02_bia_matrix.png",
  },
  {
    id: "performance-compliance",
    label: "PERFORMANCE & COMPLIANCE",
    title: "Performance & Compliance Center",
    description: "Consolidated KPI dashboard, ISO 22301 clause-level scoring, audit-readiness pack, and AI Gap Analysis — formerly three pages, now one.",
    url: "/images/showcase/03_performance_compliance.png",
  },
  {
    id: "dependency-map",
    label: "NETWORK",
    title: "D3 Dependency Map",
    description: "Force-directed graph linking processes, applications, IT functions, vendors and people — built live from the unified asset graph.",
    url: "/images/showcase/04_dependency_map.png",
  },
  {
    id: "geographic-map",
    label: "GEOSPATIAL",
    title: "Asset Geo Map",
    description: "Every asset with a location — facilities, applications, people, vendors — plotted on a single map with cluster zoom & IP-to-geo fallback.",
    url: "/images/showcase/05_geo_map.png",
  },
  {
    id: "recovery-gantt",
    label: "RECOVERY PLANS",
    title: "Generated Recovery Plans",
    description: "AI-generated Business Continuity Plans tied to each BIA — Enterprise BCP, IT-DR, departmental plans with process / risk / gap / task counts and one-click approval.",
    url: "/images/showcase/06_recovery_gantt.png",
  },
];

// (prefers-reduced-motion no longer hard-disables autoplay — the slideshow
// rotates regardless. CSS-level animations could be muted if needed in the
// future via a single matchMedia check, but right now both the fade and the
// progress bar respect users' motion preferences via their own CSS.)

export default function PlatformInActionSection() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [progressPct, setProgressPct] = useState(0);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);        // live mirror so the interval reads fresh value
  const politeResumeRef = useRef(null);   // setTimeout id for polite resume
  const sectionRef = useRef(null);        // for IntersectionObserver
  const inViewportRef = useRef(true);     // assume in-view until told otherwise

  // Keep pausedRef in sync without retearing the interval
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // ── Main tick: progress + slide flip (mounted ONCE) ─────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden || pausedRef.current || !inViewportRef.current) return;
      setProgressPct((p) => {
        const next = p + (100 * TICK_MS) / SLIDE_MS;
        if (next >= 100) {
          setActiveIdx((i) => (i + 1) % showcaseSlides.length);
          return 0;
        }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);

  // ── Pause when scrolled off-screen ──────────────────────────────────────
  useEffect(() => {
    if (!sectionRef.current || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => { inViewportRef.current = entry.isIntersecting; },
      { threshold: 0.1 }
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  // ── Preload + idle prefetch the other 5 images ──────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Slide 2 — high-priority preload
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = showcaseSlides[1].url;
    document.head.appendChild(link);

    // Slides 3-6 — staggered idle prefetch
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1000));
    const tasks = [];
    for (let i = 2; i < showcaseSlides.length; i++) {
      const id = idle(() => {
        const img = new Image();
        img.src = showcaseSlides[i].url;
      }, { timeout: 4000 });
      tasks.push(id);
    }
    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
      const cancel = window.cancelIdleCallback || clearTimeout;
      tasks.forEach((id) => cancel(id));
    };
  }, []);

  // ── Manual jump: 8s polite pause then resume ────────────────────────────
  const activeIdxRef = useRef(0);
  useEffect(() => { activeIdxRef.current = activeIdx; }, [activeIdx]);

  const jumpTo = useCallback((idx) => {
    setActiveIdx(idx);
    setProgressPct(0);
    setPaused(true);
    if (politeResumeRef.current) clearTimeout(politeResumeRef.current);
    politeResumeRef.current = setTimeout(() => {
      politeResumeRef.current = null;
      setPaused(false);
    }, POLITE_PAUSE_MS);
  }, []);

  const goPrev = useCallback(() => {
    const n = showcaseSlides.length;
    jumpTo((activeIdxRef.current - 1 + n) % n);
  }, [jumpTo]);

  const goNext = useCallback(() => {
    const n = showcaseSlides.length;
    jumpTo((activeIdxRef.current + 1) % n);
  }, [jumpTo]);

  // ── Keyboard nav (←/→) when frame is focused ────────────────────────────
  const onKeyDown = useCallback((e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
  }, [goPrev, goNext]);

  // ── Touch swipe (≥50 px horizontal delta) ───────────────────────────────
  const touchStartXRef = useRef(null);
  const onTouchStart = useCallback((e) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
    setPaused(true);
  }, []);
  const onTouchEnd = useCallback((e) => {
    const start = touchStartXRef.current;
    const end = e.changedTouches[0]?.clientX;
    if (start !== null && end !== undefined) {
      const dx = end - start;
      if (dx > 50) goPrev();
      else if (dx < -50) goNext();
    }
    touchStartXRef.current = null;
  }, [goPrev, goNext]);

  // (Hover/focus pause removed — autoplay is unconditional unless user
  // explicitly interacts. document.hidden + IntersectionObserver still gate it.)

  useEffect(() => {
    return () => {
      if (politeResumeRef.current) clearTimeout(politeResumeRef.current);
    };
  }, []);

  const slide = showcaseSlides[activeIdx];

  return (
    <section ref={sectionRef} className="py-10 sm:py-14 px-4 sm:px-6 lg:px-8 bg-zinc-50" data-testid="platform-in-action-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-5 sm:mb-7">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 mb-2">
            See It In Action
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 tracking-tight mb-2">
            One Platform. Total Operational Visibility.
          </h2>
          <p className="text-sm text-slate-600 max-w-2xl mx-auto leading-relaxed">
            From mission control to dependency mapping — explore the modules that make ResiliencyHUB the most comprehensive BCM platform available.
          </p>
        </div>

        {/* Stage */}
        <div
          className="max-w-5xl mx-auto"
          role="region"
          aria-roledescription="carousel"
          aria-label="Platform features showcase"
          data-testid="platform-slideshow"
        >
          {/* Image frame — fixed aspect, no layout shift. Uses object-contain so
              the full dashboard screenshot is always visible (no crop). bg-slate-50
              fills any letterbox gap and matches each screenshot's neutral surround. */}
          <div
            className="group relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 shadow-2xl shadow-slate-900/10 outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onKeyDown={onKeyDown}
            tabIndex={0}
            role="group"
            aria-roledescription="slide"
            aria-label={`${activeIdx + 1} of ${showcaseSlides.length}: ${slide.title}`}
          >
            <div className="aspect-[2/1] bg-slate-50 relative max-h-[55vh] mx-auto">
              {showcaseSlides.map((s, i) => {
                const isActive = i === activeIdx;
                return (
                  <img
                    key={s.id}
                    src={s.url}
                    alt={`${s.title} — ${s.description.slice(0, 90)}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    fetchpriority={i === 0 ? "high" : "auto"}
                    className={`absolute inset-0 w-full h-full object-contain transition-[opacity,transform] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isActive
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-[1.035] pointer-events-none"
                    }`}
                    aria-hidden={isActive ? "false" : "true"}
                    data-testid={`slide-image-${s.id}`}
                  />
                );
              })}

              {/* (Brand mask removed per user — dashboard screenshots render
                  as-is. When you provide re-shot screenshots with the new
                  ResiliencyHUB branding baked in, no overlay is needed.) */}

              {/* Live / Paused indicator (top-right; top-left now hosts brand mask). */}
              <div className="absolute top-3 right-3 z-20 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide shadow-sm border border-white/60 select-none flex items-center gap-2" aria-hidden="true">
                <span className={`inline-block w-2 h-2 rounded-full ${paused ? "bg-amber-500" : "bg-emerald-500 animate-pulse"}`} />
                <span className={paused ? "text-amber-700" : "text-emerald-700"}>
                  {paused ? "Paused" : "Auto-playing"}
                </span>
                <span className="text-slate-400 font-medium">· {activeIdx + 1} / {showcaseSlides.length}</span>
              </div>

              {/* Prev / Next chevrons — fade-in on hover (desktop); always visible on touch */}
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous slide"
                data-testid="slide-prev-btn"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-10 sm:h-10 rounded-full bg-white/90 backdrop-blur-sm border border-slate-200 shadow-md text-slate-700 flex items-center justify-center transition-all duration-200 ease-out hover:scale-110 hover:bg-white hover:shadow-lg focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:opacity-100 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 max-[640px]:opacity-100"
              >
                <ChevronLeft className="w-5 h-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next slide"
                data-testid="slide-next-btn"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-10 sm:h-10 rounded-full bg-white/90 backdrop-blur-sm border border-slate-200 shadow-md text-slate-700 flex items-center justify-center transition-all duration-200 ease-out hover:scale-110 hover:bg-white hover:shadow-lg focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:opacity-100 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 max-[640px]:opacity-100"
              >
                <ChevronRight className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Content card under image */}
          <div
            className="mt-4 sm:mt-5 px-2 sm:px-6 text-center max-w-3xl mx-auto min-h-[88px]"
            aria-live="polite"
            data-testid="slide-content"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700 mb-1.5" data-testid="slide-eyebrow">
              {slide.label} · <span className="text-slate-400 font-semibold">{activeIdx + 1} / {showcaseSlides.length}</span>
            </p>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1.5" data-testid="slide-title">
              {slide.title}
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed" data-testid="slide-description">
              {slide.description}
            </p>
          </div>

          {/* Progress dots */}
          <div className="mt-4 flex items-center justify-center gap-2" role="tablist" aria-label="Slide navigation">
            {showcaseSlides.map((s, i) => {
              const isActive = i === activeIdx;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-current={isActive ? "true" : undefined}
                  aria-label={`Go to slide ${i + 1}: ${s.title}`}
                  onClick={() => jumpTo(i)}
                  data-testid={`slide-dot-${i}`}
                  className={`relative h-1.5 rounded-full transition-all duration-300 overflow-hidden ${
                    isActive
                      ? "w-10 bg-slate-200"
                      : "w-2 bg-slate-300 hover:bg-slate-400"
                  }`}
                >
                  {isActive && (
                    <span
                      className="absolute inset-y-0 left-0 bg-sky-700 transition-[width] duration-75 ease-linear"
                      style={{ width: `${Math.min(100, progressPct)}%` }}
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* SR-only mirror for crawlers + screen readers */}
          <ul className="sr-only" aria-hidden="true" data-testid="slide-seo-mirror">
            {showcaseSlides.map((s) => (
              <li key={`seo-${s.id}`}>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
